import React from "react"
import { useTranslation } from "react-i18next"
import FormElement from "components/Form/FormElement"
import { jsonHash } from "utils/hash"

export const BikeCollectionImportForm = ({
  partner,
  setUpdatedPartner,
  errors,
  setErrors,
  isEdit,
  isViewMode,
  editCallback,
  showLocalErrors,
  setShowLocalErrors
}) => {
  const [formFields, setFormFields] = React.useState(partner)
  const defaultFormEvents = React.useMemo(() => ({
    ready: true,
    // when form is in edition mode, by default the editable is set at false
    // when the form is not in edition mode, it means creation mode, the form should be editable
    editable: !isEdit,
    viewMode: isViewMode || false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isEdit])
  const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
  const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

  const { t } = useTranslation()

  React.useEffect(() => {
    // When partner reloaded (after await fetchData()), set data in formFields
    if(partner) setFormFields(partner)
  },
  [partner])

React.useEffect(() => {
    // When we create a new partner, we have to pass data to parent directly.
    // We don't have any submit button on this component in this case
    if(!isEdit) setUpdatedPartner((prevFormFields) => ({
      ...prevFormFields,
      import_allowed: formFields.import_allowed,
    }))
},
// eslint-disable-next-line react-hooks/exhaustive-deps
[isEdit, formFields])

  const handleInputChange = React.useCallback((event) => {
    const target = event.target
    const value = (target.value === "true")
    const name = target.name

    setFormFields(s => ({
        ...s,
        [name]: value
    }))
  }, [])

  const handleValidation = React.useCallback((name, errs) => {
    setErrors((s) => {
      const cleanErrors = [...s.filter((e) => e.origin !== name)]
      return [
          ...cleanErrors,
          ...errs
      ]
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleEditableForm = (event) => {
    event.preventDefault()
    setFormEvents({
        ...formEvents,
        editable: !formEvents.editable
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    // Validate inputs
    const formReady = !(errors.length > 0)

    if (!formReady) {
        setShowLocalErrors(true)
    }
    else {
        // hide error
        setShowLocalErrors(false)
        // prevent form to be submitted again
        setFormEvents(s => ({
            ...s,
            ready: false
        }))

        const updatedPartner = {
          ...formFields,
          import_allowed: formFields.import_allowed,
      }

      await editCallback(updatedPartner)

        setFormEvents(s => ({
            ...s,
            ready: true,
            editable: false
        }))
    }
  }

  return (
    <form>
      <section className="c-card p-5 md:p-12 mb-7">
        <h2 className="c-h2 font-semibold mb-7 uppercase">{t("title_detail_entity", { ns: "bikeCollectionImport" })}</h2>
        <div className="l-grid u-mg-negative-hz-m">
          <div className="lg:w-1/2 w-full p-5">
            <FormElement
              value={formFields.import_allowed}
              name="import_allowed"
              type="radio"
              label={t("edit.bike_collection_import_label", { ns: "partner" }) }
              options={[
                  { value: true, label: t("global.yes") },
                  { value: false, label: t("global.no") }
              ]}
              classNameOptionsContainer="flex-row w-full"
              classNameLabel="mb-5"
              classNameOption="flex-1 items-center"
              required={true}
              showErrors={showLocalErrors}
              onValidate={handleValidation}
              onChange={handleInputChange}
              readonly={!formEvents.editable}
            />
          </div>
        </div>
        { isEdit && !isViewMode &&
          <div className="flex justify-end">
            {formEvents.editable
              ?
                <>
                  <button
                    type="button"
                    className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
                    onClick={toggleEditableForm}
                  >
                    {t("global.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="c-btn c-btn--primary c-btn--m px-12 mx-5"
                    onClick={handleSubmit}
                    disabled={!formEvents.ready || !formHasChanged}
                  >
                    {t("global.save")}
                  </button>
                </>
              :
                <button
                  type="button"
                  className="c-btn c-btn--primary px-12 mx-5"
                  onClick={toggleEditableForm}
                >
                  {t("global.update")}
                </button>
            }
          </div> }
      </section>
    </form>
  )
}